import { toast } from "react-toastify";

import axios, { AxiosResponse } from "axios";
import { DATA_SVC_RS_ADDRESS } from "constants/app.constants";

import { FilterCountT, FilterModel, IQuery } from "models/filter";

import {
  isPlainIncludePredicate,
  withoutBufferOnExclusion
} from "components/filter/util/shapefileFilterPredicate";

import { Result } from "./dataSource";
import { MapExtent } from "./useTownshipRangeGrid";

const filterServiceEndPoint = process.env.REACT_APP_FILTER_SERVICE;
let mapBoxFilter;

async function getPlaysList() {
  const playsUrl = `${DATA_SVC_RS_ADDRESS}/api/v1/data/plays`;
  const response = await axios.get(playsUrl);
  if (response.status !== 200) {
    return null;
  }
  const data = response.data;
  return data.sort();
}

let controller: AbortController = null;
// `abortHolder` lets a caller bring its own single-flight slot instead of
// sharing the module-level `controller` above. Without this, two unrelated
// features that both call this function (e.g. the base well layer's own
// filter post and the well-list/geo-job filter posts) would cancel each
// other's in-flight request whenever they happened to overlap, even though
// neither one is a "newer" version of the other's call — see EVA-35 PR #6601
// review thread on baseLayerFilter.ts. Existing callers that don't pass a
// holder keep exactly the old shared-controller behaviour.
export interface FilterAbortHolder {
  current: AbortController | null;
  /**
   * Suppresses the filtering-error/backend-services toasts below. The
   * toasts are right for a user-initiated post — the well-list filter, a
   * geo-calc/geo-sim job — but the base well layer (EVA-35) posts unprompted
   * on mount, on org change and on preference change, so a transient filter
   * service blip must not surface as "an error occurred while filtering" to a
   * user who didn't touch anything.
   */
  suppressErrorToast?: boolean;
}
async function getFilterFromQuery(
  node,
  abortHolder: FilterAbortHolder = null
): Promise<Result<string>> {
  try {
    // Cancel this slot's own previous in-flight call (if any) and open a new
    // one — a caller with its own holder never touches, or gets touched by,
    // an unrelated caller's request.
    if (abortHolder) {
      if (abortHolder.current) {
        abortHolder.current.abort();
      }
      abortHolder.current = new AbortController();
    } else {
      if (controller) {
        controller.abort();
      }
      controller = new AbortController();
    }
    const signal = abortHolder ? abortHolder.current.signal : controller.signal;
    const response = await axios.post(filterServiceEndPoint, node, {
      signal
    });
    if (response.status !== 200) {
      return {
        ok: false,
        error: response?.data ?? "We're sorry, an error occurred."
      };
    }
    return {
      ok: true,
      value: response.data
    };
  } catch (err) {
    if (axios.isCancel(err)) {
      return {
        ok: false,
        error: "cancelled"
      };
    }
    if (
      !abortHolder?.suppressErrorToast &&
      (!err.response || err.response.status !== 401)
    ) {
      if (err.response && err.response.status === 503) {
        toast.error(
          "We're sorry, EVA is unable to communicate with backend services at this moment."
        );
      } else {
        toast.error("An error occurred while filtering.");
      }
    }

    return {
      ok: false,
      error: err?.response?.data ?? "We're sorry, an error occurred."
    };
  }
}

async function getFilter(queryObj) {
  const localQueryObj = JSON.parse(JSON.stringify(queryObj));
  //localQueryObj.forEach((filter) => deconst e filter.id);

  const fullQueryObj = {
    operator: "and",
    predicates: [...localQueryObj, mapBoxFilter],
    children_nodes: []
  };

  const node = {
    job_id: "test",
    user_id: "test1",
    query: fullQueryObj
  };

  const response = await axios.post(filterServiceEndPoint, node);

  return response.data;
}

// The map view is the one polygon nobody stamps: a lasso is marked
// "selection" where it is drawn, and everything reads "no source" as "this is
// the viewport". That convention is fine inside the app, but it leaves the
// server unable to tell the viewport apart from a polygon some other caller
// sent with no source of its own, so it says so on the way out instead. Only
// the outgoing copy is marked — the polygon held in state keeps carrying no
// source, which is what saved filters, undo, and the map-move comparison all
// read.
const MAP_VIEW_POLYGON_SOURCE = "mapView";

/**
 * A shapefile predicate carries its own match mode, written when the user set
 * it and never restamped from anything else, so the request build has nothing
 * to add: the stored predicates go out exactly as they are.
 *
 * `stampMapView` is opt-in, and only the two live filter runs ask for it. An
 * unmarked polygon is the viewport only when it came from the map the user is
 * looking at. A saved filter written before the lasso got its own marker holds
 * an unmarked lasso, and the geo job screens hand a stored polygon straight to
 * this builder and keep the well list the request returns — calling either of
 * those the map view would narrow a stored result and write the narrowed one
 * down.
 */
function generateFilterQuery(
  wellList = [],
  polygon,
  excludePolygons,
  properties,
  previousFilterId,
  screenshotPolygon,
  entityKind,
  stampMapView = false
): FilterModel {
  const predicates = [];
  let children = [];
  const operator = "and";
  // The polygon goes in ahead of the well list because the tree is drawn in
  // this order, and a filter reads as the area first and the list inside it.
  if (polygon) {
    predicates.push(
      stampMapView && !polygon.source
        ? { ...polygon, source: MAP_VIEW_POLYGON_SOURCE }
        : polygon
    );
  }
  for (const filter of wellList) {
    predicates.push(filter);
  }
  if (screenshotPolygon) {
    predicates.push(screenshotPolygon);
  }
  if (excludePolygons && excludePolygons.length > 0) {
    predicates.push(...excludePolygons);
  }
  if (properties && properties.length > 0) {
    const byPropPredicates = {};
    properties.forEach((prop) => {
      const predicates = prop.predicates;
      predicates.forEach((pred) => {
        const property = pred.property;
        if (!(property in byPropPredicates)) {
          byPropPredicates[property] = [];
        }
        // A shapefile buffer on an excluding predicate is read as no buffer
        // everywhere it is shown, so it is not sent either; one that is sent
        // goes out rounded and capped, the way it is read.
        byPropPredicates[property].push(withoutBufferOnExclusion(pred));
      });
    });
    //child is AND with a bunch of OR child predicates
    children = Object.keys(byPropPredicates).map((prop) => {
      const pred = byPropPredicates[prop];
      const first = pred.length > 0 ? pred[0] : null;

      // Predicates on one property are ORed together, except when they take
      // wells out: those have to be ANDed, or each one would add back what the
      // last removed. What counts as taking wells out is the same set the
      // buffer strip above reads, so the two cannot drift apart — plus any
      // other "not …" spelling, which this routing has always covered and
      // which nothing about a buffer needs to know.
      const operator =
        first != null &&
        (!isPlainIncludePredicate(first) || first.operator.startsWith("not "))
          ? "and"
          : "or";

      return {
        operator,
        predicates: byPropPredicates[prop],
        children_nodes: []
      };
    });
  }
  const query: IQuery = {
    operator,
    predicates,
    children_nodes: children
  };

  return {
    // The job_id and user_id are part of legacy code that's not used anymore.
    job_id: "test",
    user_id: "test1",
    query,
    previousFilterId: previousFilterId,
    entityKind: entityKind,
    scenarioIds: []
  };
}
function getFilterCountsFromFilterId(filterId: string) {
  const endpoint = `${filterServiceEndPoint}/counts/${filterId}`;
  return axios.get<FilterCountT>(endpoint);
}

export interface UwiInfo {
  formattedId: string;
  uniqueId: string;
  sortingId: string;
}
export interface WellListResult {
  uwiList: UwiInfo[];
  boundingBox: MapExtent;
  resourcePlay: string[];
  totalCount?: number;
}
// Single-entry promise cache — deduplicates concurrent calls for the same filterId, evicted on reject or new filterId.
let wellsCache: {
  filterId: string;
  promise: Promise<AxiosResponse<WellListResult>>;
} | null = null;

function getWellsFromFilterId(filterId: string): Promise<AxiosResponse<WellListResult>> {
  if (!filterId) {
    return undefined;
  }
  if (wellsCache && wellsCache.filterId === filterId) {
    return wellsCache.promise;
  }
  const endpoint = `${DATA_SVC_RS_ADDRESS}/api/v1/uwi/${filterId}`;
  const promise = axios.get<WellListResult>(endpoint);
  promise.catch(() => {
    if (wellsCache?.promise === promise) {
      wellsCache = null;
    }
  });
  wellsCache = { filterId, promise };
  return promise;
}

function getWellsFromFilterIdPaginated(
  filterId: string,
  offset: number,
  limit: number,
  // Nothing single-flights this read, so a superseding caller needs the signal to
  // cancel an older in-flight GET — otherwise it resolves last and wins.
  signal?: AbortSignal
): Promise<AxiosResponse<WellListResult>> {
  if (!filterId) return undefined;
  const endpoint = `${DATA_SVC_RS_ADDRESS}/api/v1/uwi/${filterId}?offset=${offset}&limit=${limit}`;
  return axios.get<WellListResult>(endpoint, signal ? { signal } : undefined);
}

/**
 * Header.WellName by uniqueId for one page of a filter's well list — the same
 * offset/limit page getWellsFromFilterIdPaginated returns. Wells with no well
 * name, and Future Location sticks, are absent.
 */
async function getWellNamesFromFilterIdPaginated(
  filterId: string,
  offset: number,
  limit: number
): Promise<Record<string, string>> {
  const endpoint = `${DATA_SVC_RS_ADDRESS}/api/v1/uwi/${filterId}/well-names?offset=${offset}&limit=${limit}`;
  const response = await axios.get<{ wellNames: Record<string, string> }>(endpoint);
  return response.data.wellNames;
}

function getUwisInFilter(
  filterId: string,
  uwis: string[]
): Promise<AxiosResponse<string[]>> {
  const endpoint = `${DATA_SVC_RS_ADDRESS}/api/v1/uwi/${filterId}/contains`;
  return axios.post<string[]>(endpoint, uwis);
}

export interface FilterMetadata {
  totalCount: number;
  contentHash: string;
  boundingBox: MapExtent;
  resourcePlays: string[];
}

function getFilterMetadata(filterId: string): Promise<AxiosResponse<FilterMetadata>> {
  if (!filterId) return undefined;
  const endpoint = `${DATA_SVC_RS_ADDRESS}/api/v1/uwi/${filterId}/metadata`;
  return axios.get(endpoint);
}

// Posts the filter itself rather than its id: the id's Redis entry can expire
// while the list is still on screen, and the copy would fail with it.
async function copyWellsFromFilter(
  filter: FilterModel,
  format: string,
  style: string,
  quoteType: string
): Promise<string> {
  if (!filter) return "";
  const endpoint = `${DATA_SVC_RS_ADDRESS}/api/v1/uwi/copy`;
  const response = await axios.post(endpoint, filter, {
    params: { format, style, quoteType },
    headers: { Accept: "text/plain" },
    responseType: "text",
    // Bound the wait so a hung backend can't leave the copy spinner stuck
    // indefinitely; surfaces as an ECONNABORTED error the caller reports.
    timeout: 60000
  });
  return response.data;
}

export {
  getFilter,
  generateFilterQuery,
  getFilterFromQuery,
  getFilterCountsFromFilterId,
  getWellsFromFilterId,
  getWellsFromFilterIdPaginated,
  getWellNamesFromFilterIdPaginated,
  getFilterMetadata,
  getPlaysList,
  copyWellsFromFilter,
  getUwisInFilter
};
